import React, { useState, useMemo, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';

import deleteIcon from '../../../../../../Assets/images/delete-icon.svg';
import filterIcon from '../../../../../../Assets/images/filter-icon.svg';
import clearIcon from '../../../../../../Assets/images/clear-icon.svg';
import editIcon from "../../../../../../Assets/images/edit-icon.svg";
import plusIcon from '../../../../../../Assets/images/plus-icon.svg';
import closeRedIcon from "../../../../../../Assets/images/close-red.svg";
import calendarIcon from "../../../../../../Assets/images/calender.svg";

import AddSupervisorModal from '../AddSupervisorModal/AddSupervisorModal';
import EditSupervisorModal from '../EditSupervisorModal/EditSupervisorModal';
import ClipLoader from "react-spinners/ClipLoader";
import ConfirmationModal from '../../../../../../Components/ConfirmationBox/ConfirmationModal';
import apiClient from '../../../../../../Config/apiClient';
import { showErrorToast, showSuccessToast } from '../../../../../../Context/Toaster/Toaster';
import { DATE_FORMAT, DATE_MONTH_FORMAT } from '../../../Utils/Constants';

const supervisorsPath = process.env.REACT_APP_CC_SUPERVISORS_BASE_PATH;
const rolesPath = process.env.REACT_APP_CC_ROLES_BASE_PATH;

const SupervisorsList = ({ supervisors, loading, refreshSupervisorList, canEditSupervisor }) => {
    const [supervisorsPerPage, setSupervisorsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [editSupervisor, setEditSupervisor] = useState(null);
    const [selectedSupervisorIds, setSelectedSupervisorIds] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [filterRoles, setFilterRoles] = useState([]);
    const [filterRoleId, setFilterRoleId] = useState('');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const [filtersApplied, setFiltersApplied] = useState(false);

    const filterRef = useRef(null);

    useEffect(() => {
        apiClient.get(`${rolesPath}/list`)
            .then(res => {
                if (res.data.status === 'SUCCESS') {
                    setFilterRoles(res.data.data);
                }
            })
            .catch(() => showErrorToast("Failed to fetch roles"));
    }, []);

    useEffect(() => {
        const handleClickOutside = e => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setShowFilterDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const totalSupervisors = supervisors.length;
    const totalPages = Math.ceil(totalSupervisors / supervisorsPerPage);
    const startIndex = (currentPage - 1) * supervisorsPerPage;

    const currentSupervisors = useMemo(
        () => supervisors.slice(startIndex, startIndex + supervisorsPerPage),
        [supervisors, startIndex, supervisorsPerPage]
    );

    const selectAllChecked = useMemo(
        () => currentSupervisors.length > 0 && currentSupervisors.every(s => selectedSupervisorIds.includes(s.id)),
        [selectedSupervisorIds, currentSupervisors]
    );

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleSelectAllChange = (e) => {
        const ids = currentSupervisors.map(s => s.id);
        if (e.target.checked) {
            setSelectedSupervisorIds(prev => [...new Set([...prev, ...ids])]);
        } else {
            setSelectedSupervisorIds(prev => prev.filter(id => !ids.includes(id)));
        }
    };

    const handleSupervisorCheckboxChange = (id) => {
        setSelectedSupervisorIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleDeleteSupervisors = async () => {
        try {
            const response = await apiClient.put(`${supervisorsPath}/delete`, {
                supervisor_ids: selectedSupervisorIds,
            });

            if (response.data.status === 'SUCCESS') {
                showSuccessToast(response.data.message || 'Supervisors deleted successfully.');
                setSelectedSupervisorIds([]);
                refreshSupervisorList();
            } else {
                showErrorToast(response.data.message || 'Failed to delete supervisors.');
            }
        } catch (err) {
            showErrorToast('Error deleting supervisors');
        } finally {
            setShowDeleteConfirm(false);
        }
    };

    const applyFilter = () => {
        const getUTCDateTime = (dateStr, isEnd = false) => {
            if (!dateStr) return undefined;
            const localDate = new Date(dateStr + (isEnd ? 'T23:59:59' : 'T00:00:00'));
            return localDate.toISOString();
        };

        const payload = {
            role_id: filterRoleId || undefined,
            start_date: getUTCDateTime(filterStartDate),
            end_date: getUTCDateTime(filterEndDate, true),
        };

        apiClient.post(`${supervisorsPath}/list`, payload)
            .then(res => {
                if (res.data.status === 'SUCCESS') {
                    refreshSupervisorList(res.data.data);
                    setCurrentPage(1);
                    setFiltersApplied(!!(filterRoleId || filterStartDate || filterEndDate));
                }
            })
            .catch(() => {
                showErrorToast("Failed to apply filters");
            })
            .finally(() => {
                setShowFilterDropdown(false);
            });
    };

    const isAnyFilterApplied = filterRoleId || filterStartDate || filterEndDate;

    const clearFilters = (e) => {
        e?.preventDefault();
        setFilterRoleId('');
        setFilterStartDate('');
        setFilterEndDate('');
        setFiltersApplied(false);

        apiClient.post(`${supervisorsPath}/list`)
            .then(res => {
                if (res.data.status === 'SUCCESS') {
                    refreshSupervisorList(res.data.data);
                    setCurrentPage(1);
                }
            })
            .catch(() => showErrorToast("Failed to clear filters"));
    };

    return (
        <main className="full-wraper">
            <div className="main-wraper">
                <div className="container-fluid">
                    <div className="main-inner">
                        <div className="common-top">
                            {canEditSupervisor && (
                                <a href="#" onClick={e => {
                                    e.preventDefault();
                                    if (selectedSupervisorIds.length > 0) {
                                        setShowDeleteConfirm(true);
                                    }
                                }} className={selectedSupervisorIds.length === 0 ? 'disabled-delete' : ''} style={{ pointerEvents: selectedSupervisorIds.length === 0 ? 'none' : 'auto', opacity: selectedSupervisorIds.length === 0 ? 0.5 : 1 }}>
                                    <img className="ct-icon" src={deleteIcon} alt="Delete" /> Delete
                                </a>
                            )}
                            <div className="filter-cvr" ref={filterRef}>
                                <a className="filter-btn" href="#" onClick={e => { e.preventDefault(); setShowFilterDropdown(prev => !prev); }}>
                                    <span className="filter-icon-wrapper">
                                        <img className="ct-icon" src={filterIcon} alt="Filter" />
                                        {filtersApplied && <span className="filter-indicator-dot"></span>}
                                    </span>
                                    Filter
                                </a>
                                <a className="clear-btn" href="#" onClick={clearFilters}>
                                    <img className="ct-icon" src={clearIcon} alt="Clear" />
                                    Clear Filter
                                </a>

                                {showFilterDropdown && (
                                    <ul className="dropdown-menu filterdrop-body show">
                                        <div className="filterdrop-split">
                                            <label className="filter-label">Permission
                                                {filterRoleId && (
                                                    <img className="red-close" src={closeRedIcon} alt="Clear role" onClick={() => setFilterRoleId('')} />
                                                )}
                                            </label>
                                            <select className="filter-select" value={filterRoleId} onChange={e => setFilterRoleId(e.target.value)}>
                                                <option value="">--Select--</option>
                                                {filterRoles.map(r => (
                                                    <option key={r.id} value={r.id}>{r.role_name}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="filterdrop-split">
                                            <label className="filter-label">Created Date
                                                {(filterStartDate || filterEndDate) && (
                                                    <img className="red-close" src={closeRedIcon} alt="Clear dates"
                                                        onClick={() => {
                                                            setFilterStartDate('');
                                                            setFilterEndDate('');
                                                        }} />
                                                )}
                                            </label>
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="filter-datepicker from-date">
                                                    <DatePicker
                                                        selected={filterStartDate ? new Date(filterStartDate) : null}
                                                        onChange={(date) => {
                                                            const formatted = date?.toLocaleDateString('en-CA');
                                                            setFilterStartDate(formatted);
                                                        }}
                                                        placeholderText="Start Date"
                                                        className="filter-date"
                                                        dateFormat={DATE_MONTH_FORMAT}
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                        popperProps={{ strategy: 'fixed' }}
                                                    />
                                                    <span className="filter-calendar-icon"><img src={calendarIcon} alt="Calendar" /></span>
                                                </div>
                                                <div className="filter-datepicker to-date">
                                                    <DatePicker
                                                        selected={filterEndDate ? new Date(filterEndDate) : null}
                                                        onChange={(date) => {
                                                            const formatted = date?.toLocaleDateString('en-CA');
                                                            setFilterEndDate(formatted);
                                                        }}
                                                        placeholderText="End Date"
                                                        className="filter-date"
                                                        dateFormat={DATE_MONTH_FORMAT}
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                        popperProps={{ strategy: 'fixed' }}
                                                    />
                                                    <span className="filter-calendar-icon"><img src={calendarIcon} alt="Calendar" /></span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="filterdrop-split mb-0">
                                            <div className="filter-buttons">
                                                <a href="#" className="filter-clear" onClick={clearFilters}>Clear</a>
                                                <a href="#" className="filter-apply" onClick={e => { e.preventDefault(); applyFilter(); }}>Apply</a>
                                            </div>
                                        </div>
                                    </ul>
                                )}
                            </div>
                            {canEditSupervisor && (
                                <a className="post-button adds-button" href="#" data-bs-toggle="modal" data-bs-target="#addSupervisorModal">
                                    {/* <img src={plusIcon} alt="Add" /> */}
                                    Add Supervisor
                                </a>
                            )}
                        </div>

                        <div className="table-cvr">
                            <div className="table-inner">
                                <table className="table" id="roleTable">
                                    <thead>
                                        <tr>
                                            <th className='col-checkbox'><input type="checkbox" className="common-check table-checkbox" checked={selectAllChecked} onChange={handleSelectAllChange} /></th>
                                            <th className='col-hashindex'>#</th>
                                            <th className='col-primary'>First Name</th>
                                            <th className='col-primary'>Last Name</th>
                                            <th className='col-primary'>Email</th>
                                            <th className='col-secondary'>Contact No.</th>
                                            <th className='col-tertiary'>Permission</th>
                                            <th className='col-tertiary'>Created</th>
                                            <th className='col-linkicon'></th>
                                            <th className='col-spacer'></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr><td colSpan="9" className="text-center py-4"><ClipLoader size={30} color="gray" /></td></tr>
                                        ) : currentSupervisors.length > 0 ? (
                                            currentSupervisors.map((s, i) => (
                                                <tr key={s.id}>
                                                    <td className='col-checkbox'><input type="checkbox" className="common-check table-checkbox" checked={selectedSupervisorIds.includes(s.id)} onChange={() => handleSupervisorCheckboxChange(s.id)} /></td>
                                                    <td className='col-hashindex'>{startIndex + i + 1}</td>
                                                    <td className='col-primary'><a className="table-link" href="#" onClick={e => { e.preventDefault(); setEditSupervisor(s); }} data-bs-toggle="modal" data-bs-target="#editSupervisorModal">{s.first_name}</a> </td>
                                                    <td className='col-primary'>{s.last_name}</td>
                                                    <td className='col-primary'>{s.email_id}</td>
                                                    <td className='col-secondary'>{s.contact_no}</td>
                                                    <td className='col-tertiary'>{s.role?.role_name}</td>
                                                    <td className='col-tertiary'>{moment(s.created_date_time).local().format(DATE_FORMAT)}</td>
                                                    <td className='col-linkicon'>
                                                        {canEditSupervisor && (
                                                            <a href="#" onClick={e => { e.preventDefault(); setEditSupervisor({ ...s, openInEditMode: true }); }} data-bs-toggle="modal" data-bs-target="#editSupervisorModal">
                                                                <img className="tb-icon" src={editIcon} alt="Edit" />
                                                            </a>
                                                        )}
                                                    </td>
                                                    <td className='col-spacer'></td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr><td colSpan="9" className="text-center py-4 text-muted">No supervisors found.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="dt-layout-row d-flex align-items-center justify-content-between mt-3">
                                <div className="dt-length">
                                    Showing{' '}
                                    <select className="dt-input" value={supervisorsPerPage} onChange={e => { setCurrentPage(1); setSupervisorsPerPage(Number(e.target.value)); }}>
                                        {[5, 10, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>{' '}
                                    of {totalSupervisors} entries
                                </div>

                                <div className="dt-paging d-flex align-items-center" style={{ gap: '5px' }}>
                                    <button className="dt-paging-button previous" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>Previous</button>
                                    {Array.from({ length: totalPages }).map((_, i) => (
                                        <button key={i + 1} className={`dt-paging-button ${currentPage === i + 1 ? "current" : ""}`} onClick={() => handlePageChange(i + 1)}>{i + 1}</button>
                                    ))}
                                    <button className="dt-paging-button next" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>Next</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <AddSupervisorModal refreshSupervisorList={refreshSupervisorList} />
                <EditSupervisorModal supervisor={editSupervisor} refreshSupervisorList={refreshSupervisorList} canEditSupervisor={canEditSupervisor} />
                <ConfirmationModal
                    id="deleteConfirmModal"
                    show={showDeleteConfirm}
                    title="Delete Supervisors"
                    message={`Are you sure you want to delete ${selectedSupervisorIds.length} Supervisor(s)?`}
                    confirmText="Yes"
                    cancelText="Cancel"
                    onConfirm={handleDeleteSupervisors}
                    onCancel={() => setShowDeleteConfirm(false)}
                />
            </div>
        </main>
    );
};

export default SupervisorsList;